/**
 * Iter-237 — Distribution Lists (Send-to-My-Team)
 * Route: /client/settings/distribution-lists
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Mail, Star, Loader2, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

export default function DistributionLists() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState({});  // {listId: {email, name, role}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/insights/distribution-lists');
      setLists(r.data.lists || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lists');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createList = useCallback(async () => {
    const name = window.prompt('New list name (e.g. Field Sales Team):');
    if (!name) return;
    try {
      await api.post('/insights/distribution-lists', { name });
      toast.success('List created');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const setDefault = useCallback(async (listId) => {
    try {
      await api.patch(`/insights/distribution-lists/${listId}`, { is_default: true });
      toast.success('Default list updated');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const removeList = useCallback(async (listId) => {
    if (!window.confirm('Delete this list and all its recipients?')) return;
    try {
      await api.delete(`/insights/distribution-lists/${listId}`);
      toast.success('List deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const addEmail = useCallback(async (listId) => {
    const form = adding[listId] || {};
    if (!form.email) { toast.error('Email is required'); return; }
    try {
      await api.post(`/insights/distribution-lists/${listId}/emails`, {
        email: form.email, display_name: form.name, role_label: form.role,
      });
      setAdding((p) => ({ ...p, [listId]: {} }));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [adding, load]);

  const removeEmail = useCallback(async (listId, emailId) => {
    try {
      await api.delete(`/insights/distribution-lists/${listId}/emails/${emailId}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="distribution-lists-page">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <button onClick={() => navigate('/client/insights')}
            className="text-cyan-300 text-sm flex items-center mb-3 hover:opacity-80"
            data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Insights
          </button>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-300/70">
                Report Distribution
              </div>
              <h1 className="text-2xl md:text-4xl font-bold mt-1">Send-to-My-Team Lists</h1>
              <p className="text-sm text-slate-300 mt-1">
                Add anyone in your org to receive quarterly reports — no platform seat required.
              </p>
            </div>
            <Button onClick={createList} className="bg-cyan-300 text-slate-900 hover:bg-cyan-400"
              data-testid="new-list-btn">
              <Plus className="w-4 h-4 mr-1" /> New List
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-6 space-y-4">
        {lists.length === 0 && (
          <p className="text-sm text-slate-500">No lists yet — create one with the button above.</p>
        )}
        {lists.map((l, i) => (
          <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-4"
            data-testid={`distribution-list-${i}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-500" />
                  <div className="text-base font-semibold text-slate-900">{l.name}</div>
                  {l.is_default && (
                    <Badge className="bg-amber-100 text-amber-900">
                      <Star className="w-3 h-3 mr-1" /> DEFAULT
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(l.recipients || []).length} recipient(s)
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!l.is_default && (
                  <Button variant="outline" size="sm"
                    onClick={() => setDefault(l.id)}
                    data-testid={`set-default-${i}`}>
                    Set Default
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => removeList(l.id)}
                  className="text-rose-600" data-testid={`delete-list-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <ul className="mt-3 space-y-1.5">
              {(l.recipients || []).map((r) => (
                <li key={r.id}
                  className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-1.5">
                  <div>
                    <span className="font-medium text-slate-900">{r.email}</span>
                    {r.display_name && <span className="text-slate-500 ml-2">{r.display_name}</span>}
                    {r.role_label && <span className="text-xs text-slate-400 ml-2">· {r.role_label}</span>}
                  </div>
                  <button onClick={() => removeEmail(l.id, r.id)}
                    className="text-rose-500 text-xs hover:underline"
                    data-testid={`remove-recipient-${r.id}`}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-2 mt-3">
              <Input placeholder="email@brand.com"
                value={adding[l.id]?.email || ''}
                onChange={(e) => setAdding((p) => ({ ...p, [l.id]: { ...p[l.id], email: e.target.value } }))}
                className="flex-1 min-w-[180px]"
                data-testid={`add-recipient-input-${i}`} />
              <Input placeholder="Display name"
                value={adding[l.id]?.name || ''}
                onChange={(e) => setAdding((p) => ({ ...p, [l.id]: { ...p[l.id], name: e.target.value } }))}
                className="flex-1 min-w-[140px]"
                data-testid={`add-recipient-name-${i}`} />
              <Input placeholder="Role (Marketing Manager)"
                value={adding[l.id]?.role || ''}
                onChange={(e) => setAdding((p) => ({ ...p, [l.id]: { ...p[l.id], role: e.target.value } }))}
                className="flex-1 min-w-[160px]"
                data-testid={`add-recipient-role-${i}`} />
              <Button onClick={() => addEmail(l.id)} data-testid={`add-recipient-btn-${i}`}>
                <Mail className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
